'use client';

import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  WEAPON_ICON_SRC, GRENADE_ICON_SRC, SMOKE_ICON_SRC, FLASH_ICON_SRC, KNIFE_ICON_SRC, MEDKIT_ICON_SRC,
} from '../spriteLayout';
import { FaCrosshairs, FaShieldAlt, FaBolt, FaUsers, FaCoins, FaGem, FaClock, FaHandshake, FaSignOutAlt, FaTrophy, FaLightbulb } from '../../icons';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations, { SEARCH_TIPS } from './RushMid.translations';
import { AtlasIcon } from './shared/AtlasIcon';

const Img: FC<{ src: string; size?: number }> = ({ src, size = 26 }) => (
  <AtlasIcon src={src} size={size} className="select-none" />
);

/**
 * Icon per search tip, index-aligned with {@link SEARCH_TIPS} (same concept order across all
 * languages). Reuses the rules' weapon/utility art plus the stance-coloured glyphs, so a tip
 * carries the same icon the player sees in battle. Keep this list in sync when editing the tips.
 */
export const TIP_ICONS: ReactNode[] = [
  <FaCrosshairs className="text-[#ff6a6a]" />,       // 0 · stances RPS
  <Img src={KNIFE_ICON_SRC} />,                       // 1 · knife
  <Img src={SMOKE_ICON_SRC} />,                       // 2 · smoke shields an ally
  <Img src={FLASH_ICON_SRC} />,                       // 3 · flash blinds the enemy
  <Img src={SMOKE_ICON_SRC} />,                       // 4 · smoke won't save from nade/knife
  <Img src={GRENADE_ICON_SRC} />,                     // 5 · grenade breaks armor
  <Img src={MEDKIT_ICON_SRC} />,                      // 6 · medkit comeback
  <FaUsers className="text-sky-300" />,               // 7 · outnumbered rally
  <FaUsers className="text-sky-300" />,               // 8 · don't dogpile one target
  <FaShieldAlt className="text-[#5aa0ff]" />,         // 9 · defense reflects
  <FaBolt className="text-[#f2b84b]" />,              // 10 · maneuver cuts accuracy
  <FaShieldAlt className="text-[#5aa0ff]" />,         // 11 · heavy armor
  <FaCoins className="text-amber-400" />,             // 12 · equal budget
  <FaCrosshairs className="text-[#ff6a6a]" />,        // 13 · read the opponent
  <FaCrosshairs className="text-[#ff6a6a]" />,        // 14 · shooters lock a target
  <FaCoins className="text-amber-400" />,             // 15 · prize split by damage
  <FaUsers className="text-sky-300" />,               // 16 · smoke/flash team-only
  <Img src={KNIFE_ICON_SRC} />,                       // 17 · knife is free every round
  <Img src={GRENADE_ICON_SRC} />,                     // 18 · grenade clips a neighbour
  <Img src={WEAPON_ICON_SRC.awp} size={30} />,        // 19 · AWP all-or-nothing
  <Img src={WEAPON_ICON_SRC.lmg} size={30} />,        // 20 · machine gun
  <FaGem className="text-cyan-300" />,                 // 21 · roulette bank per ticket
  <Img src={KNIFE_ICON_SRC} />,                        // 22 · knife executes the wounded
  <FaClock className="text-white/80" />,               // 23 · timeout → auto-shoot Attack
  <FaHandshake className="text-white/80" />,           // 24 · both wiped = draw refund
  <FaSignOutAlt className="text-accent-red" />,        // 25 · leave = forfeit
  <FaTrophy className="text-amber-400" />,             // 26 · rating + replay
  <FaCrosshairs className="text-sky-300" />,           // 27 · tap-to-pick target
];

if (process.env.NODE_ENV !== 'production' && TIP_ICONS.length !== SEARCH_TIPS.en.length) {
  // a mismatch means a tip was added/removed without updating the icon list → silent icon drift
  console.warn(`TIP_ICONS (${TIP_ICONS.length}) is out of sync with SEARCH_TIPS (${SEARCH_TIPS.en.length})`);
}

/** One tip line — icon in a chip, text beside it. Same shape as the rules' IconRow so the
 *  search screen and the "how to play" sheet read as one family. */
export const TipRow: FC<{ icon: ReactNode; children: ReactNode }> = ({ icon, children }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2 text-start">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center">{icon}</div>
    <p className="text-sm leading-snug text-white/90">{children}</p>
  </div>
);

/** Rotating gameplay tip shown above the radar while matchmaking. */
export const RushMidSearchTip: FC = () => {
  const { t, lang } = useLocalTranslation(translations);
  const tips = SEARCH_TIPS[lang];
  // start on a random tip and jump to a random OTHER one each tick — no fixed order
  const [i, setI] = useState(() => Math.floor(Math.random() * tips.length));
  // advance to a random OTHER tip (never repeats back-to-back)
  const next = useCallback(() => {
    setI((n) => {
      if (tips.length <= 1) return n;
      let k = Math.floor(Math.random() * (tips.length - 1));
      if (k >= n) k += 1;
      return k;
    });
  }, [tips.length]);
  // (re)arm the auto-advance timer, replacing any running one
  const timer = useRef(0);
  const arm = useCallback(() => {
    window.clearInterval(timer.current);
    timer.current = window.setInterval(next, 5500);
  }, [next]);
  useEffect(() => { arm(); return () => window.clearInterval(timer.current); }, [arm]);
  // tapping advances AND restarts the timer, so an auto-tick that was about to fire
  // can't immediately swap the tip out from under your tap.
  const advance = useCallback(() => { next(); arm(); }, [next, arm]);
  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-2">
      <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
        <FaLightbulb size={14} /> {t('tipLabel')}
      </div>
      {/* tap the tip to skip to the next one (with a small fade+slide) */}
      <button
        type="button"
        onClick={advance}
        className="w-full bg-transparent p-0 outline-none active:opacity-70"
        aria-label={t('tipLabel')}
      >
        <div key={i} className="animate-shot-tip-in">
          <TipRow icon={TIP_ICONS[i]}>{tips[i]}</TipRow>
        </div>
      </button>
    </div>
  );
};
